import React, { useState } from 'react'
import { IInvoice, kraviaOnboardStatus, kraviaRegisterInvoice } from '@query'
import { Button, ModalV2 } from '@components'
import { OnboardingView } from '@pages/InvoicingPage/DebtCollection/trackerMessages/Onboarding/OnboardingView'
import { FaMoneyBillTrendUp } from 'react-icons/fa6'
import { Trans, useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useBusinessContext } from '@context'
import styled from 'styled-components'
import { formatCurrency, formatDate } from '@utils'
import { toast } from 'react-hot-toast'

interface Props {
  invoice: IInvoice
  phase: 'waiting' | 'ready'
  handoverAt: string
}

const MINIMUM_AMOUNT_TO_REGISTER = 1

const RegisterDescription: React.FC = () => {
  const { t } = useTranslation()

  return (
    <>
      <Title>{t('invoicing.debtCollection.register.title')}</Title>
      <Copy>
        <Trans
          i18nKey="invoicing.debtCollection.register.description1"
          components={[
            <a
              key="debt-collection-link"
              href={t('link.docs.debtCollection')}
              target="_blank"
              rel="noreferrer"
            />
          ]}
        />
      </Copy>
      <Copy>
        <Trans i18nKey="invoicing.debtCollection.register.description2" />
      </Copy>
    </>
  )
}

export const StartDebtCollectionMessage: React.FC<Props> = ({ invoice, phase, handoverAt }) => {
  const { t } = useTranslation()
  const [isOnboardingVisible, setIsOnboardingVisible] = useState(false)
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const hasTooSmallAmount =
    invoice?.total_amount + invoice?.total_vat_amount < MINIMUM_AMOUNT_TO_REGISTER

  const { data } = useQuery({
    queryKey: [slug, 'kravia_onboard_status'],
    queryFn: () => kraviaOnboardStatus({ slug })
  })

  const { mutateAsync, isPending: isRegistering } = useMutation<unknown, unknown, number>({
    mutationFn: invoiceId => kraviaRegisterInvoice({ slug, invoiceId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [slug, 'invoices', invoice.id] }),
        queryClient.invalidateQueries({ queryKey: [slug, 'kravia_invoice_details', invoice?.id] })
      ])
    },
    onError: () => {
      toast.error(t('general.error'))
    }
  })

  const renderMainContent = () => {
    switch (phase) {
      case 'waiting':
        return (
          <>
            <Title>{t('invoicing.debtCollection.register.tooEarly')}</Title>
            <Copy>
              {t('invoicing.debtCollection.register.tooEarlyInfo1', {
                date: formatDate(handoverAt)
              })}
            </Copy>
          </>
        )
      case 'ready':
        break
      default: {
        const exhaustive: never = phase
        return exhaustive
      }
    }

    if (!data?.kravia_verified) {
      return (
        <>
          <RegisterDescription />
          <Actions>
            <Button
              type="button"
              size="sm"
              icon={<FaMoneyBillTrendUp />}
              onClick={() => setIsOnboardingVisible(true)}
              intent="primary"
            >
              {t('invoicing.actions.onboardToKravia')}
            </Button>
          </Actions>
        </>
      )
    }

    return (
      <>
        <RegisterDescription />
        <Actions>
          <Button
            type="button"
            size="sm"
            icon={<FaMoneyBillTrendUp />}
            onClick={() => mutateAsync(invoice.id)}
            disabled={isRegistering || hasTooSmallAmount}
            showSpinner={isRegistering}
            intent="primary"
          >
            {t('invoicing.actions.sendToDebtCollection')}
          </Button>
        </Actions>
        {hasTooSmallAmount && (
          <Warning>
            {t('invoicing.debtCollection.register.totalAmountWarning', {
              amount: formatCurrency(MINIMUM_AMOUNT_TO_REGISTER)
            })}
          </Warning>
        )}
      </>
    )
  }

  return (
    <>
      {renderMainContent()}

      <ModalV2
        isVisible={isOnboardingVisible}
        handleOnClose={() => setIsOnboardingVisible(false)}
        width={600}
        height={800}
        header={t('invoicing.debtCollection.onboard.title')}
      >
        <OnboardingView data={data} onRegisterInvoice={() => mutateAsync(invoice.id)} />
      </ModalV2>
    </>
  )
}

const Title = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.menuBg};
`

const Copy = styled.p`
  margin: ${({ theme }) => theme.spacing.xs}rem 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.iconGray};

  a {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`

const Warning = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.dashboardNeg};
`
